export interface ProductVariant {
  code: string;
  spec: string;
  price: number;
  audPrice: number;
}

export interface ProductFamily {
  id: string;
  name: string;
  categories: string[];
  variants: ProductVariant[];
}

export const CATEGORIES = [
  { id: "all", label: "All", emoji: "🧬" },
  { id: "glp-1", label: "GLP-1", emoji: "💉" },
  { id: "hgh", label: "HGH", emoji: "💪" },
  { id: "repair", label: "Repair", emoji: "🔧" },
  { id: "anti-aging", label: "Anti-Aging", emoji: "⏳" },
  { id: "immune-neuro", label: "Immune/Neuro", emoji: "🛡" },
  { id: "other", label: "Other", emoji: "📋" },
] as const;

export const products: ProductFamily[] = [
  // ==================== A ====================
  {
    id: "adamax",
    name: "ADAMAX",
    categories: ["anti-aging"],
    variants: [
      { code: "AX5", spec: "5mg×10vials", price: 103, audPrice: 146 },
      { code: "AX10", spec: "10mg×10vials", price: 150, audPrice: 213 },
    ],
  },
  {
    id: "ahk-cu",
    name: "AHK-CU",
    categories: ["repair", "anti-aging"],
    variants: [
      { code: "AU20", spec: "20mg×10vials", price: 45, audPrice: 64 },
      { code: "AU50", spec: "50mg×10vials", price: 58, audPrice: 82 },
      { code: "AU100", spec: "100mg/vial, 10vial/kit", price: 94, audPrice: 133 },
    ],
  },
  {
    id: "aicar",
    name: "AICAR",
    categories: ["immune-neuro"],
    variants: [
      { code: "Aicar50", spec: "50mg×10vials", price: 35, audPrice: 50 },
      { code: "Aicar100", spec: "100mg×10vials", price: 63, audPrice: 89 },
      { code: "AR50", spec: "50mg×10vials", price: 35, audPrice: 50 },
      { code: "AR100", spec: "100mg×10vials", price: 67, audPrice: 95 },
    ],
  },
  {
    id: "aod9604",
    name: "AOD9604",
    categories: ["other"],
    variants: [
      { code: "5AD", spec: "5mg×10vials", price: 126, audPrice: 179 },
      { code: "10AD", spec: "10mg×10vials", price: 193, audPrice: 274 },
    ],
  },
  {
    id: "ara290",
    name: "ARA 290 (Cibinetide)",
    categories: ["other"],
    variants: [
      { code: "RA10", spec: "10mg×10vials", price: 126, audPrice: 179 },
      { code: "RA16", spec: "16mg×10vials", price: 177, audPrice: 251 },
    ],
  },
  {
    id: "acetic-acid",
    name: "Acetic Acid Water 0.6%",
    categories: ["other"],
    variants: [
      { code: "AA3", spec: "3ml×10vials", price: 9, audPrice: 13 },
      { code: "AA5", spec: "5ml/vial, 10vial/kit", price: 5, audPrice: 7 },
      { code: "AA10", spec: "10ml/vial, 10vial/kit", price: 11, audPrice: 16 },
    ],
  },
  {
    id: "adipotide",
    name: "Adipotide",
    categories: ["other"],
    variants: [
      { code: "AP2", spec: "2mg×10vials", price: 37, audPrice: 53 },
      { code: "AP5", spec: "5mg×10vials", price: 63, audPrice: 89 },
      { code: "AP10", spec: "10mg×10vials", price: 151, audPrice: 214 },
    ],
  },
  {
    id: "adipotide-fttp",
    name: "Adipotide / FTTP",
    categories: ["other"],
    variants: [
      { code: "ADP2", spec: "2mg×10vials", price: 61, audPrice: 87 },
      { code: "ADP5", spec: "5mg×10vials", price: 107, audPrice: 152 },
      { code: "ADP10", spec: "10mg×10vials", price: 187, audPrice: 266 },
    ],
  },
  // ==================== B ====================
  {
    id: "b12",
    name: "B-12",
    categories: ["other"],
    variants: [
      { code: "B12", spec: "10mg×10vials", price: 45, audPrice: 64 },
    ],
  },
  {
    id: "b733",
    name: "B7-33",
    categories: ["anti-aging"],
    variants: [
      { code: "B72", spec: "2mg×10vials", price: 40, audPrice: 57 },
      { code: "B710", spec: "5mg×10vials", price: 95, audPrice: 135 },
    ],
  },
  {
    id: "bac",
    name: "Benzyl Alcohol 0.9%",
    categories: ["other"],
    variants: [
      { code: "BAC3(BA3)", spec: "3ml/vial, 10vial/kit", price: 7, audPrice: 10 },
      { code: "BAC10(BA10)", spec: "10ml/vial, 10vial/kit", price: 8, audPrice: 11 },
    ],
  },
  {
    id: "bronchogen",
    name: "Bronchogen",
    categories: ["immune-neuro"],
    variants: [
      { code: "BR20", spec: "20mg/vial, 10vial/kit", price: 139, audPrice: 197 },
    ],
  },
  {
    id: "bpc157",
    name: "BPC-157 (Acetate)",
    categories: ["repair"],
    variants: [
      { code: "BC2", spec: "2mg×10vials", price: 19, audPrice: 27 },
      { code: "BC5", spec: "5mg×10vials", price: 61, audPrice: 87 },
      { code: "BC10", spec: "10mg×10vials", price: 79, audPrice: 112 },
      { code: "BC20", spec: "20mg×10vials", price: 115, audPrice: 163 },
    ],
  },
  {
    id: "bpctb",
    name: "BPC-157 + TB-500",
    categories: ["repair"],
    variants: [
      { code: "BB10", spec: "BPC5mg+TB5mg×10vials", price: 144, audPrice: 204 },
      { code: "BB20", spec: "BPC10mg+TB10mg×10vials", price: 234, audPrice: 332 },
      { code: "BB30", spec: "BPC15mg+TB15mg×10vials", price: 337, audPrice: 479 },
      { code: "BB40", spec: "BPC20mg+TB20mg×10vials", price: 440, audPrice: 625 },
    ],
  },
  {
    id: "glow70",
    name: "BPC157 10mg + GHK-CU 50mg + TB500 10mg",
    categories: ["repair"],
    variants: [
      { code: "GLOW70", spec: "70mg×10vials", price: 238, audPrice: 338 },
    ],
  },
  {
    id: "klow80",
    name: "BPC157 10mg + GHK-CU 50mg + TB500 10mg + KPV 10mg",
    categories: ["repair"],
    variants: [
      { code: "KLOW80(BBKG80)", spec: "80mg×10vials", price: 278, audPrice: 395 },
    ],
  },
  // ==================== C ====================
  {
    id: "cagri-sema",
    name: "Cagrilintide 5mg + Semaglutide 5mg",
    categories: ["glp-1"],
    variants: [
      { code: "CS10", spec: "10mg×10vials", price: 119, audPrice: 169 },
      { code: "CS20", spec: "20mg×10vials", price: 229, audPrice: 325 },
    ],
  },
  {
    id: "cagri-sema-v2",
    name: "Cagri-Sema (2.5mg + 2.5mg)",
    categories: ["glp-1"],
    variants: [
      { code: "CS5", spec: "5mg×10vials", price: 121, audPrice: 172 },
    ],
  },
  {
    id: "cagrilintide",
    name: "Cagrilintide",
    categories: ["glp-1"],
    variants: [
      { code: "CGL2", spec: "2mg×10vials", price: 46, audPrice: 65 },
      { code: "CGL5", spec: "5mg×10vials", price: 139, audPrice: 197 },
      { code: "CGL10", spec: "10mg×10vials", price: 247, audPrice: 351 },
    ],
  },
  {
    id: "cardiogen",
    name: "Cardiogen",
    categories: ["anti-aging"],
    variants: [
      { code: "CA20", spec: "20mg/vial, 10vial/kit", price: 74, audPrice: 105 },
    ],
  },
  {
    id: "cartalax",
    name: "Cartalax",
    categories: ["repair"],
    variants: [
      { code: "LAX20", spec: "20mg/vial, 10vial/kit", price: 167, audPrice: 237 },
    ],
  },
  {
    id: "cerebrolysin",
    name: "Cerebrolysin",
    categories: ["anti-aging"],
    variants: [
      { code: "CBL5", spec: "5mg×10vials", price: 127, audPrice: 180 },
      { code: "CBL10", spec: "10mg×10vials", price: 198, audPrice: 281 },
      { code: "CBL20", spec: "20mg/vial, 10vial/kit", price: 337, audPrice: 479 },
    ],
  },
  {
    id: "chonluten",
    name: "Chonluten",
    categories: ["anti-aging"],
    variants: [
      { code: "CH20", spec: "20mg/vial, 10vial/kit", price: 139, audPrice: 197 },
    ],
  },
  {
    id: "cjc-dac",
    name: "CJC-1295 (With DAC)",
    categories: ["hgh"],
    variants: [
      { code: "CD2", spec: "2mg×10vials", price: 79, audPrice: 112 },
      { code: "CD5", spec: "5mg×10vials", price: 247, audPrice: 351 },
      { code: "CD10", spec: "10mg×10vials", price: 404, audPrice: 574 },
    ],
  },
  {
    id: "cjc-ipa",
    name: "CJC-1295 (Without DAC) + Ipamorelin",
    categories: ["hgh"],
    variants: [
      { code: "CP10", spec: "CJC5mg+IPA5mg×10vials", price: 144, audPrice: 204 },
      { code: "CP20", spec: "CJC10mg+IPA10mg×10vials", price: 251, audPrice: 356 },
    ],
  },
  {
    id: "cjc-nodac",
    name: "CJC-1295 (Without DAC)",
    categories: ["hgh"],
    variants: [
      { code: "CND2", spec: "2mg×10vials", price: 27, audPrice: 38 },
      { code: "CND5", spec: "5mg×10vials", price: 106, audPrice: 151 },
      { code: "CND10", spec: "10mg×10vials", price: 202, audPrice: 287 },
    ],
  },
  {
    id: "cortagen",
    name: "Cortagen",
    categories: ["immune-neuro"],
    variants: [
      { code: "COR20", spec: "20mg/vial, 10vial/kit", price: 93, audPrice: 132 },
    ],
  },
  {
    id: "crystagen",
    name: "Crystagen",
    categories: ["anti-aging"],
    variants: [
      { code: "CRY20", spec: "20mg/vial, 10vial/kit", price: 56, audPrice: 80 },
    ],
  },
  // ==================== D ====================
  {
    id: "dermorphin",
    name: "Dermorphin",
    categories: ["immune-neuro"],
    variants: [
      { code: "DR2", spec: "2mg×10vials", price: 27, audPrice: 38 },
      { code: "DR5", spec: "5mg×10vials", price: 37, audPrice: 53 },
      { code: "DR10", spec: "10mg×10vials", price: 79, audPrice: 112 },
    ],
  },
  {
    id: "dihaxa",
    name: "Dihaxa",
    categories: ["immune-neuro"],
    variants: [
      { code: "PNB0408", spec: "10mg×10vials", price: 81, audPrice: 115 },
    ],
  },
  {
    id: "dsip",
    name: "DSIP",
    categories: ["immune-neuro"],
    variants: [
      { code: "DS2", spec: "2mg/vial, 10vial/kit", price: 27, audPrice: 38 },
      { code: "DS5", spec: "5mg/vial, 10vial/kit", price: 72, audPrice: 102 },
      { code: "DS10", spec: "10mg/vial, 10vial/kit", price: 117, audPrice: 166 },
      { code: "DS15", spec: "15mg/vial, 10vial/kit", price: 63, audPrice: 89 },
      { code: "DS20", spec: "20mg/vial, 10vial/kit", price: 88, audPrice: 125 },
    ],
  },
  {
    id: "dulaglutide",
    name: "Dulaglutide",
    categories: ["glp-1"],
    variants: [
      { code: "DU5", spec: "5mg×10vials", price: 102, audPrice: 145 },
      { code: "DU10", spec: "10mg×10vials", price: 185, audPrice: 263 },
    ],
  },
  // ==================== E ====================
  {
    id: "epitalon",
    name: "Epithalon",
    categories: ["anti-aging"],
    variants: [
      { code: "ET10", spec: "10mg×10vials", price: 72, audPrice: 102 },
      { code: "ET50", spec: "50mg×10vials", price: 216, audPrice: 307 },
    ],
  },
  {
    id: "epo",
    name: "EPO",
    categories: ["other"],
    variants: [
      { code: "E3K", spec: "3000iu/vial, 5vials/kit", price: 84, audPrice: 119 },
    ],
  },
  {
    id: "elu-pp332",
    name: "ELU-PP-332",
    categories: ["anti-aging"],
    variants: [
      { code: "PP5", spec: "5mg/vial, 10vial/kit", price: 111, audPrice: 158 },
      { code: "PP10", spec: "10mg/vial, 10vial/kit", price: 127, audPrice: 180 },
    ],
  },
  // ==================== F ====================
  {
    id: "foliestatina",
    name: "FOLIESTATINA (FST-344)",
    categories: ["hgh"],
    variants: [
      { code: "FN1", spec: "1mg/vial, 10vial/kit", price: 75, audPrice: 107 },
    ],
  },
  {
    id: "f42",
    name: "F42",
    categories: ["other"],
    variants: [
      { code: "F42", spec: "2mg×10vials", price: 171, audPrice: 243 },
    ],
  },
  {
    id: "fst344",
    name: "FST 344",
    categories: ["hgh"],
    variants: [
      { code: "FST344", spec: "1mg×10vials", price: 131, audPrice: 186 },
    ],
  },
  {
    id: "foxo4",
    name: "FOXO4-DRI",
    categories: ["hgh"],
    variants: [
      { code: "F410", spec: "10mg×10vials", price: 217, audPrice: 308 },
    ],
  },
  {
    id: "frag-17-23",
    name: "Frag 17-23",
    categories: ["hgh"],
    variants: [
      { code: "F1723", spec: "10mg×10vials", price: 112, audPrice: 159 },
    ],
  },
  {
    id: "hgh-fragment",
    name: "HGH Fragment 176-191",
    categories: ["hgh"],
    variants: [
      { code: "FR1", spec: "1mg×10vials", price: 23, audPrice: 33 },
      { code: "FR2", spec: "2mg×10vials", price: 32, audPrice: 45 },
      { code: "FR5", spec: "5mg×10vials", price: 42, audPrice: 60 },
      { code: "FR10", spec: "10mg×10vials", price: 63, audPrice: 89 },
      { code: "FR12", spec: "12mg×10vials", price: 79, audPrice: 112 },
      { code: "FR15", spec: "15mg×10vials", price: 89, audPrice: 126 },
    ],
  },
  // ==================== G ====================
  {
    id: "gdf-8",
    name: "GDF-8",
    categories: ["hgh"],
    variants: [
      { code: "GDF8", spec: "1mg×10vials", price: 167, audPrice: 237 },
    ],
  },
  {
    id: "ghk-cu",
    name: "GHK-Cu",
    categories: ["repair", "anti-aging"],
    variants: [
      { code: "CU50", spec: "50mg×10vials", price: 31, audPrice: 44 },
      { code: "CU100", spec: "100mg×10vials", price: 45, audPrice: 64 },
    ],
  },
  {
    id: "ghrp2",
    name: "GHRP-2 Acetate",
    categories: ["hgh"],
    variants: [
      { code: "G25", spec: "5mg×10vials", price: 27, audPrice: 38 },
      { code: "G210", spec: "10mg×10vials", price: 34, audPrice: 48 },
    ],
  },
  {
    id: "ghrp6",
    name: "GHRP-6 Acetate",
    categories: ["hgh"],
    variants: [
      { code: "G65", spec: "5mg×10vials", price: 27, audPrice: 38 },
      { code: "G610", spec: "10mg×10vials", price: 34, audPrice: 48 },
    ],
  },
  {
    id: "glutathione",
    name: "Glutathione",
    categories: ["other"],
    variants: [
      { code: "GTT600", spec: "600mg×10vials", price: 56, audPrice: 80 },
      { code: "GTT1500", spec: "1500mg×10vials", price: 94, audPrice: 133 },
    ],
  },
  {
    id: "gonadorelin",
    name: "Gonadorelin",
    categories: ["other"],
    variants: [
      { code: "GND2", spec: "2mg×10vials", price: 23, audPrice: 33 },
    ],
  },
  // ==================== H ====================
  {
    id: "hcg",
    name: "HCG",
    categories: ["hgh"],
    variants: [
      { code: "HCG1000", spec: "1000iu×10vials", price: 23, audPrice: 33 },
      { code: "HCG2000", spec: "2000iu×10vials", price: 37, audPrice: 53 },
      { code: "HCG5000", spec: "5000iu×10vials", price: 59, audPrice: 84 },
      { code: "HCG10000", spec: "10000iu×10vials", price: 103, audPrice: 146 },
      { code: "G5K", spec: "5000iu×10vials", price: 74, audPrice: 105 },
      { code: "G10K", spec: "10000iu×10vials", price: 149, audPrice: 212 },
    ],
  },
  {
    id: "hgh-191aa",
    name: "HGH 191AA (Somatropin)",
    categories: ["hgh"],
    variants: [
      { code: "H6", spec: "6iu×10vials", price: 23, audPrice: 33 },
      { code: "H8", spec: "8iu×10vials", price: 32, audPrice: 45 },
      { code: "H10", spec: "10iu×10vials", price: 40, audPrice: 57 },
      { code: "H12", spec: "12iu×10vials", price: 50, audPrice: 71 },
      { code: "H15", spec: "15iu×10vials", price: 68, audPrice: 97 },
      { code: "H24", spec: "24iu×10vials", price: 87, audPrice: 124 },
      { code: "H36", spec: "36iu×10vials", price: 115, audPrice: 163 },
    ],
  },
  {
    id: "hexarelin",
    name: "Hexarelin Acetate",
    categories: ["hgh"],
    variants: [
      { code: "HX2", spec: "2mg×10vials", price: 38, audPrice: 54 },
      { code: "HX5", spec: "5mg×10vials", price: 71, audPrice: 101 },
    ],
  },
  {
    id: "hmg",
    name: "HMG",
    categories: ["hgh"],
    variants: [
      { code: "G75", spec: "75iu×10vials", price: 45, audPrice: 64 },
    ],
  },
  {
    id: "humanin",
    name: "Humanin",
    categories: ["hgh"],
    variants: [
      { code: "HUM10", spec: "10mg/vial, 10vial/kit", price: 103, audPrice: 146 },
    ],
  },
  // ==================== I ====================
  {
    id: "igf-des",
    name: "IGF-DES",
    categories: ["hgh"],
    variants: [
      { code: "IGF-DES", spec: "2mg×10vials", price: 54, audPrice: 77 },
    ],
  },
  {
    id: "igf1-lr3",
    name: "IGF-1 LR3",
    categories: ["hgh"],
    variants: [
      { code: "IG01", spec: "0.1mg×10vials", price: 99, audPrice: 141 },
      { code: "IG1", spec: "1mg×10vials", price: 251, audPrice: 356 },
    ],
  },
  {
    id: "insulin-1vial",
    name: "Insulin-1vial",
    categories: ["other"],
    variants: [
      { code: "ISU", spec: "3ml/1vial", price: 42, audPrice: 60 },
    ],
  },
  {
    id: "ipamorelin",
    name: "Ipamorelin",
    categories: ["hgh"],
    variants: [
      { code: "IPA2", spec: "2mg×10vials", price: 19, audPrice: 27 },
      { code: "IP5", spec: "5mg×10vials", price: 54, audPrice: 77 },
      { code: "IP10", spec: "10mg×10vials", price: 88, audPrice: 125 },
      { code: "IPA20", spec: "20mg×10vials", price: 79, audPrice: 112 },
    ],
  },
  // ==================== K ====================
  {
    id: "kisspeptin",
    name: "Kiss Peptin-10",
    categories: ["other"],
    variants: [
      { code: "KS5", spec: "5mg×10vials", price: 94, audPrice: 133 },
      { code: "KS10", spec: "10mg×10vials", price: 162, audPrice: 230 },
      { code: "KS20", spec: "20mg×10vials", price: 111, audPrice: 158 },
    ],
  },
  {
    id: "kpv",
    name: "KPV",
    categories: ["repair"],
    variants: [
      { code: "KP5", spec: "5mg×10vials", price: 54, audPrice: 77 },
      { code: "KP10", spec: "10mg×10vials", price: 81, audPrice: 115 },
    ],
  },
  // ==================== L ====================
  {
    id: "lc120",
    name: "LC120",
    categories: ["other"],
    variants: [
      { code: "LC120", spec: "10ml/vial, 10vial/kit", price: 135, audPrice: 192 },
    ],
  },
  {
    id: "lcarnitine",
    name: "L-Carnitine",
    categories: ["other"],
    variants: [
      { code: "LC400", spec: "400mg 10ml×10vials", price: 28, audPrice: 40 },
      { code: "LC600", spec: "600mg 10ml×10vials", price: 72, audPrice: 102 },
      { code: "LC1200", spec: "1200mg 10ml×10vials", price: 117, audPrice: 166 },
    ],
  },
  {
    id: "lemon-bottle",
    name: "Lemon Bottle",
    categories: ["other"],
    variants: [
      { code: "LB", spec: "10ml×10vials", price: 54, audPrice: 77 },
    ],
  },
  {
    id: "liraglutide",
    name: "Liraglutide",
    categories: ["glp-1"],
    variants: [
      { code: "LL5", spec: "5mg/vial, 10vial/kit", price: 140, audPrice: 199 },
      { code: "LL10", spec: "10mg/vial, 10vial/kit", price: 229, audPrice: 325 },
    ],
  },
  {
    id: "lipoc",
    name: "Lipo-c",
    categories: ["other"],
    variants: [
      { code: "LC216", spec: "10ml×10vials", price: 63, audPrice: 89 },
    ],
  },
  {
    id: "livagen",
    name: "Livagen",
    categories: ["anti-aging"],
    variants: [
      { code: "LI20", spec: "20mg/vial, 10vial/kit", price: 154, audPrice: 219 },
    ],
  },
  {
    id: "ll37",
    name: "LL37",
    categories: ["anti-aging"],
    variants: [
      { code: "LL37", spec: "5mg×10vials", price: 121, audPrice: 172 },
      { code: "375", spec: "5mg/vial, 10vial/kit", price: 71, audPrice: 101 },
    ],
  },
  // ==================== M ====================
  {
    id: "mazdutide",
    name: "Mazdutide",
    categories: ["glp-1"],
    variants: [
      { code: "MDT10", spec: "10mg×10vials", price: 166, audPrice: 236 },
    ],
  },
  {
    id: "melanotan1",
    name: "Melanotan I",
    categories: ["other"],
    variants: [
      { code: "MT1", spec: "10mg×10vials", price: 43, audPrice: 61 },
      { code: "MT2", spec: "10mg×10vials", price: 75, audPrice: 107 },
    ],
  },
  {
    id: "melanotan2",
    name: "Melanotan II",
    categories: ["other"],
    variants: [
      { code: "ML5", spec: "5mg/vial, 10vial/kit", price: 27, audPrice: 38 },
      { code: "MT-2", spec: "10mg/vial, 10vial/kit", price: 99, audPrice: 141 },
    ],
  },
  {
    id: "mic-lipo-c-b12",
    name: "MIC (Lipo C with B12)",
    categories: ["other"],
    variants: [
      { code: "MIC", spec: "10mg×10vials", price: 124, audPrice: 176 },
    ],
  },
  {
    id: "mgf",
    name: "MGF",
    categories: ["hgh"],
    variants: [
      { code: "FM2", spec: "2mg×10vials", price: 35, audPrice: 50 },
    ],
  },
  {
    id: "mk677",
    name: "MK677",
    categories: ["hgh"],
    variants: [
      { code: "MK75", spec: "5mg×10vials", price: 19, audPrice: 27 },
    ],
  },
  {
    id: "motsc",
    name: "MOTS-c (Human)",
    categories: ["anti-aging", "immune-neuro"],
    variants: [
      { code: "MS10", spec: "10mg×10vials", price: 99, audPrice: 141 },
      { code: "MS15", spec: "15mg×10vials", price: 126, audPrice: 179 },
      { code: "MS20", spec: "20mg×10vials", price: 157, audPrice: 223 },
      { code: "MS40", spec: "40mg×10vials", price: 278, audPrice: 395 },
    ],
  },
  // ==================== N ====================
  {
    id: "nad",
    name: "NAD+",
    categories: ["anti-aging"],
    variants: [
      { code: "NJ100", spec: "100mg×10vials", price: 38, audPrice: 54 },
      { code: "NJ500", spec: "500mg×10vials", price: 54, audPrice: 77 },
      { code: "NJ1000", spec: "1000mg×10vials", price: 81, audPrice: 115 },
    ],
  },
  // ==================== O ====================
  {
    id: "os01",
    name: "OS-01",
    categories: ["anti-aging"],
    variants: [
      { code: "OS01", spec: "100mg×10vials", price: 95, audPrice: 135 },
    ],
  },
  {
    id: "oxytocin",
    name: "Oxytocin Acetate",
    categories: ["immune-neuro"],
    variants: [
      { code: "OT1", spec: "1mg×9vials", price: 19, audPrice: 27 },
      { code: "OT2", spec: "2mg×10vials", price: 38, audPrice: 54 },
      { code: "OT5", spec: "5mg×10vials", price: 58, audPrice: 82 },
      { code: "OT10", spec: "10mg×10vials", price: 85, audPrice: 121 },
    ],
  },
  // ==================== P ====================
  {
    id: "p21",
    name: "P21",
    categories: ["immune-neuro"],
    variants: [
      { code: "P210", spec: "10mg/vial, 10vial/kit", price: 103, audPrice: 146 },
    ],
  },
  {
    id: "pancragen",
    name: "Pancragen",
    categories: ["other"],
    variants: [
      { code: "PA20", spec: "20mg/vial, 10vial/kit", price: 182, audPrice: 258 },
    ],
  },
  {
    id: "pe-22-28",
    name: "PE-22-28",
    categories: ["immune-neuro"],
    variants: [
      { code: "PE5", spec: "5mg×10vials", price: 56, audPrice: 80 },
      { code: "PE10", spec: "10ml×10vials", price: 107, audPrice: 152 },
    ],
  },
  {
    id: "peg-mgf",
    name: "PEG MGF",
    categories: ["hgh"],
    variants: [
      { code: "FMP2", spec: "2mg×10vials", price: 67, audPrice: 95 },
    ],
  },
  {
    id: "pinealon",
    name: "Pinealon",
    categories: ["immune-neuro"],
    variants: [
      { code: "PIN5", spec: "5mg/vial, 10vial/kit", price: 27, audPrice: 38 },
      { code: "PIN10", spec: "10mg/vial, 10vial/kit", price: 41, audPrice: 58 },
      { code: "PIN20", spec: "20mg/vial, 10vial/kit", price: 74, audPrice: 105 },
    ],
  },
  {
    id: "pnc-27",
    name: "PNC-27",
    categories: ["anti-aging"],
    variants: [
      { code: "PNC5", spec: "5mg×10vials", price: 56, audPrice: 80 },
      { code: "PNC10", spec: "10mg×10vials", price: 112, audPrice: 159 },
    ],
  },
  {
    id: "pt141",
    name: "PT-141",
    categories: ["immune-neuro"],
    variants: [
      { code: "P415", spec: "5mg/vial, 10vial/kit", price: 31, audPrice: 44 },
      { code: "P41", spec: "10mg/vial, 10vial/kit", price: 103, audPrice: 146 },
    ],
  },
  // ==================== R ====================
  {
    id: "retatrutide",
    name: "Retatrutide",
    categories: ["glp-1"],
    variants: [
      { code: "RT5", spec: "5mg×10vials", price: 43, audPrice: 61 },
      { code: "RT10", spec: "10mg×10vials", price: 76, audPrice: 108 },
      { code: "RT15", spec: "15mg×10vials", price: 110, audPrice: 156 },
      { code: "RT20", spec: "20mg×10vials", price: 132, audPrice: 187 },
      { code: "RT30", spec: "30mg×10vials", price: 180, audPrice: 256 },
      { code: "RT40", spec: "40mg×10vials", price: 234, audPrice: 332 },
      { code: "RT50", spec: "50mg×10vials", price: 290, audPrice: 412 },
      { code: "RT60", spec: "60mg×10vials", price: 337, audPrice: 479 },
      { code: "RT80", spec: "80mg×10vials", price: 400, audPrice: 568 },
      { code: "RT100", spec: "100mg×10vials", price: 507, audPrice: 720 },
      { code: "RT120", spec: "120mg×10vials", price: 651, audPrice: 924 },
    ],
  },
  {
    id: "retatrutide-tirzepatide",
    name: "Retatrutide 20mg + Tirzepatide 40mg",
    categories: ["glp-1"],
    variants: [
      { code: "RET60", spec: "60mg×10vials", price: 314, audPrice: 446 },
    ],
  },
  // ==================== S ====================
  {
    id: "selank",
    name: "Selank",
    categories: ["immune-neuro"],
    variants: [
      { code: "SK5", spec: "5mg×10vials", price: 54, audPrice: 77 },
      { code: "SK10", spec: "10mg×10vials", price: 90, audPrice: 128 },
      { code: "SK20", spec: "20mg/vial, 10vial/kit", price: 79, audPrice: 112 },
    ],
  },
  {
    id: "selank-semax",
    name: "Selank + Semax",
    categories: ["immune-neuro"],
    variants: [
      { code: "XS20", spec: "20mg/vial, 10vial/kit", price: 238, audPrice: 338 },
    ],
  },
  {
    id: "semax",
    name: "Semax",
    categories: ["immune-neuro"],
    variants: [
      { code: "XA5", spec: "5mg×10vials", price: 58, audPrice: 82 },
      { code: "XA10", spec: "10mg×10vials", price: 85, audPrice: 121 },
      { code: "SX20", spec: "20mg/vial, 10vial/kit", price: 71, audPrice: 101 },
    ],
  },
  {
    id: "semaglutide",
    name: "Semaglutide",
    categories: ["glp-1"],
    variants: [
      { code: "SM2", spec: "2mg/vial, 10vial/kit", price: 21, audPrice: 30 },
      { code: "SM5", spec: "5mg/vial, 10vial/kit", price: 27, audPrice: 38 },
      { code: "SM10", spec: "10mg/vial, 10vial/kit", price: 65, audPrice: 92 },
      { code: "SM15", spec: "15mg/vial, 10vial/kit", price: 49, audPrice: 70 },
      { code: "SM20", spec: "20mg/vial, 10vial/kit", price: 103, audPrice: 146 },
      { code: "SM30", spec: "30mg/vial, 10vial/kit", price: 139, audPrice: 197 },
      { code: "SM40", spec: "40mg/vial, 10vial/kit", price: 180, audPrice: 256 },
      { code: "SM50", spec: "50mg/vial, 10vial/kit", price: 213, audPrice: 302 },
      { code: "SM60", spec: "60mg/vial, 10vial/kit", price: 166, audPrice: 236 },
      { code: "SM100", spec: "100mg/vial, 10vial/kit", price: 190, audPrice: 270 },
      { code: "SM120", spec: "120mg/vial, 10vial/kit", price: 221, audPrice: 314 },
    ],
  },
  {
    id: "sermorelin",
    name: "Sermorelin",
    categories: ["hgh"],
    variants: [
      { code: "SMO2", spec: "2mg/vial, 10vial/kit", price: 42, audPrice: 60 },
      { code: "SMO5", spec: "5mg×10vials", price: 97, audPrice: 138 },
      { code: "SMO10", spec: "10mg×10vials", price: 162, audPrice: 230 },
      { code: "SMO20", spec: "20mg×10vials", price: 166, audPrice: 236 },
    ],
  },
  {
    id: "snap8",
    name: "SNAP-8",
    categories: ["anti-aging"],
    variants: [
      { code: "NP810", spec: "10mg×10vials", price: 85, audPrice: 121 },
      { code: "NP8100", spec: "100mg×10vials", price: 167, audPrice: 237 },
    ],
  },
  {
    id: "ss31",
    name: "SS-31",
    categories: ["anti-aging"],
    variants: [
      { code: "2S10", spec: "10mg×10vials", price: 115, audPrice: 163 },
      { code: "2S20", spec: "20mg×10vials", price: 209, audPrice: 297 },
      { code: "2S50", spec: "50mg×10vials", price: 404, audPrice: 574 },
    ],
  },
  {
    id: "survodutide",
    name: "Survodutide",
    categories: ["glp-1"],
    variants: [
      { code: "SUR10", spec: "10mg×10vials", price: 175, audPrice: 249 },
    ],
  },
  // ==================== T ====================
  {
    id: "tb500",
    name: "TB500 (Thymosin B4 Acetate)",
    categories: ["repair"],
    variants: [
      { code: "TB2", spec: "2mg×10vials", price: 35, audPrice: 50 },
      { code: "BT5", spec: "5mg×10vials", price: 94, audPrice: 133 },
      { code: "BT10", spec: "10mg×10vials", price: 177, audPrice: 251 },
      { code: "TB20", spec: "20mg×10vials", price: 190, audPrice: 270 },
    ],
  },
  {
    id: "tesa-ipa",
    name: "Tesa10 + IPA5",
    categories: ["hgh"],
    variants: [
      { code: "TI", spec: "15mg×10vials", price: 251, audPrice: 356 },
    ],
  },
  {
    id: "tesamorelin",
    name: "Tesamorelin",
    categories: ["hgh"],
    variants: [
      { code: "TSM2", spec: "2mg×10vials", price: 61, audPrice: 87 },
      { code: "TSM5", spec: "5mg×10vials", price: 130, audPrice: 185 },
      { code: "TSM10", spec: "10mg×10vials", price: 220, audPrice: 312 },
      { code: "TSM20", spec: "20mg×10vials", price: 375, audPrice: 533 },
    ],
  },
  {
    id: "thymalin",
    name: "Thymalin",
    categories: ["immune-neuro"],
    variants: [
      { code: "TY10", spec: "10mg×10vials", price: 115, audPrice: 163 },
      { code: "TY20", spec: "20mg×10vials", price: 91, audPrice: 129 },
    ],
  },
  {
    id: "thymosin-alpha1",
    name: "Thymosin Alpha-1",
    categories: ["immune-neuro"],
    variants: [
      { code: "TA5", spec: "5mg×10vials", price: 135, audPrice: 192 },
      { code: "TA10", spec: "10mg×10vials", price: 234, audPrice: 332 },
    ],
  },
  {
    id: "tirzepatide",
    name: "Tirzepatide",
    categories: ["glp-1"],
    variants: [
      { code: "TR5", spec: "5mg×10vials", price: 38, audPrice: 54 },
      { code: "TR10", spec: "10mg×10vials", price: 65, audPrice: 92 },
      { code: "TR15", spec: "15mg×10vials", price: 85, audPrice: 121 },
      { code: "TR20", spec: "20mg×10vials", price: 108, audPrice: 153 },
      { code: "TR30", spec: "30mg×10vials", price: 144, audPrice: 204 },
      { code: "TR40", spec: "40mg×10vials", price: 171, audPrice: 243 },
      { code: "TR50", spec: "50mg×10vials", price: 207, audPrice: 294 },
      { code: "TR60", spec: "60mg×10vials", price: 251, audPrice: 356 },
      { code: "TR80", spec: "80mg×10vials", price: 314, audPrice: 446 },
      { code: "TR100", spec: "100mg×10vials", price: 382, audPrice: 542 },
      { code: "TR120", spec: "120mg×10vials", price: 158, audPrice: 224 },
    ],
  },
  {
    id: "triparatide",
    name: "Triparatide",
    categories: ["hgh"],
    variants: [
      { code: "TER10", spec: "10mg/vial, 10vial/kit", price: 227, audPrice: 322 },
    ],
  },
  // ==================== V ====================
  {
    id: "vilon",
    name: "Vilon",
    categories: ["immune-neuro"],
    variants: [
      { code: "VI20", spec: "20mg/vial, 10vial/kit", price: 229, audPrice: 325 },
    ],
  },
  {
    id: "vip",
    name: "VIP",
    categories: ["immune-neuro"],
    variants: [
      { code: "VP5", spec: "5mg/vial, 10vial/kit", price: 171, audPrice: 243 },
      { code: "VP10", spec: "10mg/vial, 10vial/kit", price: 274, audPrice: 389 },
    ],
  },
  // ==================== Numbers & Misc ====================
  {
    id: "amino1mg",
    name: "5-amino-1mg",
    categories: ["other"],
    variants: [
      { code: "5AM", spec: "5mg/vial, 10vial/kit", price: 45, audPrice: 64 },
      { code: "10AM", spec: "10mg/vial, 10vial/kit", price: 81, audPrice: 115 },
      { code: "50AM", spec: "50mg/vial, 10vial/kit", price: 171, audPrice: 243 },
    ],
  },
  {
    id: "water",
    name: "Water",
    categories: ["other"],
    variants: [
      { code: "WA3", spec: "3ml/vial, 10vial/kit", price: 5, audPrice: 7 },
      { code: "WA10", spec: "10ml/vial, 10vial/kit", price: 7, audPrice: 10 },
    ],
  },
  {
    id: "xt100",
    name: "XT100",
    categories: ["other"],
    variants: [
      { code: "XT100", spec: "100mg×10vials", price: 178, audPrice: 253 },
    ],
  },
];